import { useEffect, useId, useRef, useState } from "react";
import formEmailMetadata from "../data/form-email-metadata.json";
import "./CreatorApplicationForm.css";

const FORMSPREE_ENDPOINT = "https://formspree.io/f/mrgnqryj";
const EMAIL_METADATA = formEmailMetadata["/creators"];
const FUTURE_SESSIONS_OPTION = "I’m interested in future sessions, keep me posted";

export default function CreatorApplicationForm() {
  const [status, setStatus] = useState("idle");
  const submitting = useRef(false);
  const successMessage = useRef(null);
  const notesHelpId = useId();

  useEffect(() => {
    if (status === "success" || status === "future-success") {
      successMessage.current?.focus();
    }
  }, [status]);

  const submit = async (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    if (submitting.current || !form.reportValidity()) return;

    submitting.current = true;
    setStatus("submitting");

    try {
      const submission = new FormData(form);
      const response = await fetch(FORMSPREE_ENDPOINT, {
        body: submission,
        headers: { Accept: "application/json" },
        method: "POST",
      });

      if (!response.ok) throw new Error("Formspree rejected the application.");
      setStatus(submission.get("cohort") === FUTURE_SESSIONS_OPTION ? "future-success" : "success");
    } catch {
      setStatus("error");
    } finally {
      submitting.current = false;
    }
  };

  if (status === "success" || status === "future-success") {
    return (
      <div
        className="creator-application-form__success"
        ref={successMessage}
        role="status"
        tabIndex={-1}
      >
        {status === "future-success" ? (
          <>
            <p>Thanks for your interest!</p>
            <p>We’ll email you about future Creator Program sessions.</p>
          </>
        ) : (
          <>
            <p>Thanks for applying!</p>
            <p>
              We’ve received your application. We’ll review it and email you about
              the upcoming cohort and next steps.
            </p>
          </>
        )}
      </div>
    );
  }

  return (
    <form
      action={FORMSPREE_ENDPOINT}
      aria-label="Creator Program application"
      aria-busy={status === "submitting"}
      className="creator-application-form"
      method="POST"
      onSubmit={submit}
    >
      <input name="subject" type="hidden" value={EMAIL_METADATA.subject} />
      <input name="form_name" type="hidden" value={EMAIL_METADATA.formName} />

      <label>
        <span>Name <small>(required)</small></span>
        <input autoComplete="name" name="name" required type="text" />
      </label>
      <label>
        <span>Email <small>(required)</small></span>
        <input autoComplete="email" name="email" required type="email" />
      </label>
      <label>
        <span>Institution <small>(required)</small></span>
        <input autoComplete="organization" name="institution" required type="text" />
      </label>
      <label>
        <span>Preferred cohort <small>(required)</small></span>
        <select defaultValue="" name="cohort" required>
          <option disabled value="">Choose a cohort</option>
          <option value="November 9–20, 2026">November 9–20, 2026</option>
          <option value="December 7–18, 2026">December 7–18, 2026</option>
          <option value="January 11–22, 2027">January 11–22, 2027</option>
          <option value="Any cohort works">Any cohort works</option>
          <option value={FUTURE_SESSIONS_OPTION}>{FUTURE_SESSIONS_OPTION}</option>
        </select>
      </label>
      <label>
        <span>Tell us a little about your research <small>(optional)</small></span>
        <span className="creator-application-form__help" id={notesHelpId}>
          A sentence or two is plenty.
        </span>
        <textarea aria-describedby={notesHelpId} name="message" rows="4" />
      </label>

      {status === "error" && (
        <p className="creator-application-form__error" role="alert">
          We couldn’t send your application. Your answers are still here. Please
          try again.
        </p>
      )}
      <button disabled={status === "submitting"} type="submit">
        {status === "submitting" ? "Sending application…" : "Apply to join"}
      </button>
    </form>
  );
}
